import { classes, icons } from '@portal/components'
import React, { memo, useCallback, useEffect, useMemo } from 'react'
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels'

import { findCollectionName } from '../../utils'
import { DocumentDetailDrawer } from '../documentDetail'
import { QueryEditor } from '../queryEditor'
import { QueryResultTable } from '../queryResultTable'
import { SideBar } from '../Sidebar'
import CollectionFilterMenu from '../Sidebar/CollectionFilterMenu'
import CollectionsList from '../Sidebar/CollectionsList'
import QueryHistoryList from '../Sidebar/QueryHistoryList'
import { TopBar } from '../topBar'
import { InitialPreviewOverlay } from '../tours'
import { useDQLEditorCollectionsContext, useDQLEditorContext } from './context'

const { Resize, SideBarClose } = icons

const CollectionsSideBar = memo(() => {
  const { sideBarOpen, setSideBarOpen } = useDQLEditorContext()

  const handleClose = useCallback(() => setSideBarOpen(false), [setSideBarOpen])

  return (
    <SideBar
      title="Collections"
      side="left"
      open={sideBarOpen}
      closeHandler={handleClose}
      closeIcon={<SideBarClose className="size-4" aria-hidden="true" />}
      headerActions={<CollectionFilterMenu />}
    >
      <CollectionsList />
    </SideBar>
  )
})

CollectionsSideBar.displayName = 'CollectionsSideBar'

export default function DQLEditorLayout() {
  const {
    query,
    sideBarOpen,
    historySideBarOpen,
    setHistorySideBarOpen,
    activeView,
    activeRow,
    result,
  } = useDQLEditorContext()

  const { activeCollection, setActiveCollection } =
    useDQLEditorCollectionsContext()

  useEffect(() => {
    const collectionName = findCollectionName(query || '')
    if (collectionName) {
      setActiveCollection(collectionName?.replace(/`/g, ''))
    }
  }, [query, result, setActiveCollection])

  const resultsTableTitle = useMemo(() => {
    if (activeCollection) return activeCollection

    if (!!result) {
      return result.queryType === 'mutation' ? 'Mutated documents' : 'Documents'
    }

    return ''
  }, [activeCollection, result])

  if (activeView === 'collections') {
    return (
      <>
        <div className="fixed inset-0 flex h-screen w-screen flex-col">
          <TopBar />

          <div className="relative flex h-full w-full">
            <CollectionsSideBar />

            <div
              className={classes(
                'relative flex h-full w-full flex-col transition-[margin,max-width] duration-200',
                {
                  'mr-56 md:mr-[500px]': !!activeRow,
                  'ml-48 md:ml-80': sideBarOpen,
                },
                // These are probably not required, but since we're doing this below for the editor
                // we might as well do it here for consistency.
                {
                  'max-w-full': !sideBarOpen,
                  'max-w-[calc(100vw-12rem)] md:max-w-[calc(100vw-20rem)]':
                    sideBarOpen && !activeRow,
                  'max-w-[calc(100vw-12rem-500px)] md:max-w-[calc(100vw-20rem-500px)]':
                    sideBarOpen && !!activeRow,
                },
              )}
              data-testid="dqlEditorAndResults"
            >
              <QueryResultTable
                title={resultsTableTitle}
                showResults={false}
                allowDelete={false}
                action="import"
                canSelectRows={false}
                sideBarOpen={sideBarOpen}
              />
            </div>

            {activeRow && <DocumentDetailDrawer />}
          </div>
        </div>

        <InitialPreviewOverlay />
      </>
    )
  }

  return (
    <>
      <div className="fixed inset-0 flex h-screen w-screen flex-col">
        <TopBar />

        <div className="relative flex h-full w-full">
          <CollectionsSideBar />

          <div
            className={classes(
              'relative flex h-full w-full flex-col transition-[margin,max-width] duration-200',
              {
                'mr-56 md:mr-[500px]': !!activeRow,
                'ml-48 md:ml-80': sideBarOpen,
                'mr-48 md:mr-80': historySideBarOpen && !activeRow,
              },
              // Dynamic max-width based on sidebar states. This is so the textarea contents wrap
              // properly, as otherwise it attempts to increase the width of the textarea to fit
              // the full width of the screen. This is only an issue on inserting very long values
              // into the textarea.
              {
                'max-w-full': !sideBarOpen && !historySideBarOpen,
                'max-w-[calc(100vw-12rem)] md:max-w-[calc(100vw-20rem)]':
                  (sideBarOpen && !historySideBarOpen && !activeRow) ||
                  (!sideBarOpen && historySideBarOpen && !activeRow),
                'max-w-[calc(100vw-12rem-20rem)] md:max-w-[calc(100vw-20rem-20rem)]':
                  (sideBarOpen && historySideBarOpen && !activeRow) ||
                  (sideBarOpen && !!activeRow),
              },
            )}
            data-testid="dqlEditorAndResults"
          >
            <PanelGroup autoSaveId="dql-editor-layout" direction="vertical">
              <Panel maxSize={80}>
                <QueryEditor />
              </Panel>
              <PanelResizeHandle className="relative h-4 w-full border-b border-solid border-border-normal">
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                  <Resize className="top-1/2 h-4 w-4 rotate-90 text-foreground-subtle" />
                </div>
              </PanelResizeHandle>
              <Panel maxSize={80}>
                <QueryResultTable sideBarOpen={true} />
              </Panel>
            </PanelGroup>
          </div>

          {activeRow && <DocumentDetailDrawer />}
          {!activeRow && activeView === 'editor' && (
            <SideBar
              title="Queries"
              side="right"
              open={historySideBarOpen}
              closeHandler={() => setHistorySideBarOpen(false)}
            >
              <QueryHistoryList />
            </SideBar>
          )}
        </div>
      </div>
    </>
  )
}
